<template>
  <div class="space-y-2">
    <!-- Position & Rotation in one row -->
    <div class="grid grid-cols-3 gap-2">
      <InheritedField
        label="X"
        :is-inherited="isInherited"
        :is-overridden="xOverridden"
        :parent-scene-name="parentSceneName"
        @reset="$emit('resetField', 'x')"
      >
        <template #default="{ showInheritedBorder }">
          <NumberField :model-value="x" @update:model-value="updateX" :disabled="disabled">
            <NumberFieldContent>
              <NumberFieldInput 
                class="h-8"
                :class="{ 'border-orange-400': showInheritedBorder }"
              />
            </NumberFieldContent>
          </NumberField>
        </template>
      </InheritedField>

      <InheritedField
        label="Y"
        :is-inherited="isInherited"
        :is-overridden="yOverridden"
        :parent-scene-name="parentSceneName"
        @reset="$emit('resetField', 'y')"
      >
        <template #default="{ showInheritedBorder }">
          <NumberField :model-value="y" @update:model-value="updateY" :disabled="disabled">
            <NumberFieldContent>
              <NumberFieldInput 
                class="h-8"
                :class="{ 'border-orange-400': showInheritedBorder }"
              />
            </NumberFieldContent>
          </NumberField>
        </template>
      </InheritedField>

      <InheritedField
        label="Rotation"
        :is-inherited="isInherited"
        :is-overridden="rotationOverridden"
        :parent-scene-name="parentSceneName"
        @reset="$emit('resetField', 'effects.rotation')"
      >
        <template #default="{ showInheritedBorder }">
          <NumberField :model-value="rotation" @update:model-value="updateRotation" :disabled="disabled">
            <NumberFieldContent>
              <NumberFieldInput 
                class="h-8" 
                placeholder="0°"
                :class="{ 'border-orange-400': showInheritedBorder }"
              />
            </NumberFieldContent>
          </NumberField>
        </template>
      </InheritedField>
    </div>

    <!-- Size -->
    <div class="grid grid-cols-2 gap-2">
      <InheritedField
        label="Width"
        :is-inherited="isInherited"
        :is-overridden="widthOverridden"
        :parent-scene-name="parentSceneName"
        @reset="$emit('resetField', 'width')"
      >
        <template #default="{ showInheritedBorder }">
          <NumberField :model-value="width" @update:model-value="updateWidth" :disabled="disabled">
            <NumberFieldContent>
              <NumberFieldInput 
                class="h-8"
                :class="{ 'border-orange-400': showInheritedBorder }"
              />
            </NumberFieldContent>
          </NumberField>
        </template>
      </InheritedField>

      <InheritedField
        label="Height"
        :is-inherited="isInherited"
        :is-overridden="heightOverridden"
        :parent-scene-name="parentSceneName"
        @reset="$emit('resetField', 'height')"
      >
        <template #default="{ showInheritedBorder }">
          <NumberField :model-value="height" @update:model-value="updateHeight" :disabled="disabled">
            <NumberFieldContent>
              <NumberFieldInput 
                class="h-8"
                :class="{ 'border-orange-400': showInheritedBorder }"
              />
            </NumberFieldContent>
          </NumberField>
        </template>
      </InheritedField>
    </div>
  </div>
</template>

<script setup lang="ts">
import InheritedField from './InheritedField.vue'
import {
  NumberField,
  NumberFieldContent,
  NumberFieldInput,
} from '@/components/ui/number-field'

interface Props {
  x: number
  y: number
  width: number
  height: number
  rotation?: number
  disabled?: boolean
  isInherited?: boolean
  xOverridden?: boolean
  yOverridden?: boolean
  widthOverridden?: boolean
  heightOverridden?: boolean
  rotationOverridden?: boolean
  parentSceneName?: string
}

const props = withDefaults(defineProps<Props>(), {
  isInherited: false,
  xOverridden: false,
  yOverridden: false,
  widthOverridden: false,
  heightOverridden: false,
  rotationOverridden: false,
  parentSceneName: 'Parent'
})

const emit = defineEmits<{
  update: [value: { x?: number; y?: number; width?: number; height?: number; rotation?: number }]
  resetField: [fieldPath: string]
}>()

const updateX = (value: number) => emit('update', { x: value })
const updateY = (value: number) => emit('update', { y: value })
const updateWidth = (value: number) => emit('update', { width: value })
const updateHeight = (value: number) => emit('update', { height: value })
const updateRotation = (value: number) => emit('update', { rotation: value })
</script>
